import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  Chip,
  TextField,
} from '@mui/material';
import { CustomUser } from 'api/models/CustomUser';
import { getInitials } from 'utilities/getInitials';
import { UserAutocompleteProps } from './filters.props';
import { modelFiltersStyles } from './ModelFilters.styles';

export const UserAutocomplete = ({
  options,
  value,
  onChange,
  onInputChange,
  inputValue,
  loading,
  label,
  placeholder,
  zoomLevel,
  textFieldSx = modelFiltersStyles.autocompleteTextField,
  disabled = false,
}: UserAutocompleteProps) => {
  const getOptionLabel = (option: CustomUser) => {
    const fullName = [option.first_name, option.last_name].filter(Boolean).join(' ');
    return fullName || option.email || option.username || '';
  };

  return (
    <Autocomplete
      size="small"
      multiple
      options={options}
      getOptionLabel={getOptionLabel}
      slotProps={{
        popper: {
          sx: modelFiltersStyles.getAutocompletePopper(zoomLevel),
        },
      }}
      disableCloseOnSelect
      disabled={disabled}
      value={value}
      onChange={onChange}
      onInputChange={onInputChange}
      inputValue={inputValue}
      loading={loading}
      limitTags={-1}
      renderInput={params => (
        <TextField 
          {...params} 
          size="small" 
          label={label}
          variant="outlined" 
          placeholder={value.length === 0 ? placeholder : ''}
          sx={textFieldSx}
        />
      )}
      renderTags={tagValue => {
        if (tagValue.length === 0) {
          return null;
        }
        
        const maxVisible = 2;
        const visibleItems = tagValue.slice(0, maxVisible);
        const remainingCount = tagValue.length - maxVisible;
        
        return (
          <Box sx={modelFiltersStyles.getTagsContainer(tagValue.length)}>
            {visibleItems.map(option => (
              <Avatar 
                key={option.id} 
                sx={modelFiltersStyles.avatar}
              >
                {getInitials({ name: `${option.first_name} ${option.last_name}` })}
              </Avatar>
            ))}
            {remainingCount > 0 && (
              <Chip
                label={`+${remainingCount}`}
                size="small"
                sx={modelFiltersStyles.chip}
              />
            )}
          </Box>
        );
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;
        return (
          <li key={option.id} {...optionProps} style={modelFiltersStyles.optionListItem}>
            <Checkbox style={modelFiltersStyles.optionCheckbox} checked={selected} />
            {[option.first_name, option.last_name].join(' ') || option.email || option.username || ''}
          </li>
        );
      }}
      sx={modelFiltersStyles.autocompleteRoot}
      isOptionEqualToValue={(option, val) => option.id === val.id}
    />
  );
};

